import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { Task as SchemaTask } from "@shared/schema";
import { useBusinesses } from "./use-businesses";

interface TaskQueryParams {
  status?: string;
  priority?: string;
}

export type Task = Omit<SchemaTask, "dueDate" | "createdAt" | "updatedAt" | "completedAt"> & {
  dueDate: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
};

export const useTasks = (params: TaskQueryParams = {}) => {
  const queryClient = useQueryClient();
  const { activeBusiness } = useBusinesses();
  const businessId = activeBusiness?.id;
  
  // Construct query string from params
  const queryParams = new URLSearchParams();
  if (params.status) queryParams.append("status", params.status);
  if (params.priority) queryParams.append("priority", params.priority);
  
  const queryString = queryParams.toString();
  const endpoint = businessId 
    ? `/api/businesses/${businessId}/tasks${queryString ? `?${queryString}` : ""}`
    : "";
  
  // Fetch tasks
  const { data: tasks = [], isLoading } = useQuery<Task[]>({
    queryKey: ["tasks", businessId],
    queryFn: async () => {
      console.log("Fetching tasks from:", endpoint);
      const response = await apiRequest("GET", endpoint);
      const data = await response.json();
      console.log("Tasks API response:", data);
      return Array.isArray(data) ? data : [];
    },
    enabled: !!businessId,
  });
  
  // Create task
  const { mutateAsync: createTask, isPending: isPendingCreate } = useMutation({
    mutationFn: async (task: Partial<Task>) => {
      if (!businessId) throw new Error("No active business selected");
      
      // Prepare a clean task object that exactly matches the server's schema expectations
      const cleanTask: {
        title: string;
        description: string | null;
        status: string;
        priority: string;
        recurring: boolean;
        dueDate?: string;
        recurrencePattern?: string;
      } = {
        title: task.title || "",
        description: task.description || null,
        status: task.status || "pending",
        priority: task.priority || "medium",
        recurring: Boolean(task.recurring || false)
      };
      
      // Only add optional fields if they are present and valid
      if (task.dueDate) {
        // Handle date in string format - server expects a valid timestamp
        try {
          // If it's already a string, use it as is; if it's a Date, convert to ISO
          cleanTask.dueDate = new Date(task.dueDate).toISOString();
        } catch (e) {
          console.warn("Invalid date format ignored:", task.dueDate);
        }
      }
      
      // Only add recurrence pattern if task is recurring
      if (cleanTask.recurring && task.recurrencePattern) {
        cleanTask.recurrencePattern = task.recurrencePattern;
      }
      
      console.log("Sending clean task data:", cleanTask);
      return apiRequest("POST", `/api/businesses/${businessId}/tasks`, cleanTask);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks", businessId] });
    },
    onError: (error) => {
      console.error("Task creation error details:", error);
    }
  });
  
  // Update task
  const { mutateAsync: updateTask, isPending: isPendingUpdate } = useMutation({
    mutationFn: async ({ id, ...data }: { id: number } & Partial<Task>) => {
      if (!businessId) throw new Error("No active business selected");
      
      // Keep it simple - only send essential fields that match the schema exactly
      const cleanUpdate: any = {
        title: data.title,
        description: data.description || null,
        status: data.status,
        priority: data.priority,
        recurring: Boolean(data.recurring)
      };
      
      // Handle dueDate - convert string dates to Date objects
      if (data.dueDate) {
        try {
          cleanUpdate.dueDate = typeof data.dueDate === 'string' 
            ? new Date(data.dueDate + 'T00:00:00.000Z')  // Ensure proper timezone
            : data.dueDate;
        } catch (e) {
          console.warn("Invalid date format, skipping:", data.dueDate);
        }
      }
      
      // Only add recurrence pattern if recurring is true
      if (cleanUpdate.recurring && data.recurrencePattern) {
        cleanUpdate.recurrencePattern = data.recurrencePattern;
      }
      
      console.log("Sending task update:", cleanUpdate, "for task ID:", id);
      return apiRequest("PUT", `/api/businesses/${businessId}/tasks/${id}`, cleanUpdate);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks", businessId] });
    },
    onError: (error) => {
      console.error("Task update error details:", error);
    }
  });
  
  // Toggle task completion
  const { mutateAsync: toggleTask, isPending: isPendingToggle } = useMutation({
    mutationFn: async (id: number) => {
      if (!businessId) throw new Error("No active business selected");
      const task = tasks.find(t => t.id === id);
      if (!task) throw new Error("Task not found");
      
      const newStatus = task.status === "completed" ? "pending" : "completed";
      
      return apiRequest("PATCH", `/api/businesses/${businessId}/tasks/${id}`, {
        status: newStatus,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks", businessId] });
    },
  });
  
  // Delete task
  const { mutateAsync: deleteTask, isPending: isPendingDelete } = useMutation({
    mutationFn: async (id: number) => {
      if (!businessId) throw new Error("No active business selected");
      return apiRequest("DELETE", `/api/businesses/${businessId}/tasks/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks", businessId] });
    },
  });
  
  return {
    tasks,
    isLoading,
    createTask,
    updateTask,
    toggleTask,
    deleteTask,
    isPendingCreate,
    isPendingUpdate,
    isPendingToggle,
    isPendingDelete,
  };
};
